
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { swalErrNotification, swalNotification } from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  IBienBanGiaiTheResponseBase,
  ICapSuaChuaResponseBase,
  IDinhMucCoDinhNewResponseBase,
  IVatTuResponseBaseWithApprovedStatus,
} from "@/core/interfaces/ApiResponses";
import { BienBanGiaiTheService } from "@/core/services/BienBanGiaiThe.service";
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";

import DinhMucCoDinhSection from "@/components/dinh-muc-co-dinh-section/DinhMucCoDinhSection.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import PhieuXuatKhoTable from "@/components/phieu-xuat-kho/PhieuXuatKhoTable.vue";
import { openBienBanGiaiThePdf } from "@/components/shared/pdf-viewer/open-bien-ban-giai-the-pdf";
// import VatTuNgoaiDinhMucTable from '@/components/ngoai-dinh-muc/VatTuNgoaiDinhMucTable.vue';
import Can from "@/components/Can.vue";
import {
  BIEN_BAN_GIAI_THE,
  PHIEU_XUAT_KHO,
} from "@/core/constants/permissions";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import VatTuNgoaiDinhMucTableV2 from "@/components/ngoai-dinh-muc/VatTuNgoaiDinhMucTableV2.vue";
import Notice from "@/components/Notice.vue";
import BienBanApproveButton from "@/components/shared/bien-ban-approve-button/BienBanApproveButton.vue";
import BienBanCompleteButton from "@/components/shared/bien-ban-complete-button/BienBanCompleteButton.vue";
import BienBanConfirmButton from "@/components/shared/bien-ban-confirm-button/BienBanConfirmButton.vue";
import {
  useBienBan,
  shouldShowConfirmButtonForLevel,
  shouldShowApproveButtonForLevel,
} from "@/core/hooks/use-bien-ban-hook";
import usePermission from "@/core/hooks/usePermission";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import PageHeaderApprovals from "@/layout/header/PageHeaderApprovals.vue";
import {
  faFileCirclePlus,
  faPencil,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCan as faTrash } from "@fortawesome/free-regular-svg-icons";

export default defineComponent({
  name: "bien-ban-giai-the-detail",

  components: {
    BaseLink,
    BienBanConfirmButton,
    DetailTopicLottie,
    BienBanApproveButton,
    BienBanCompleteButton,
    ButtonCustom,
    ConfirmationDialog,
    DinhMucCoDinhSection,
    LabelCustom,
    Notice,
    PageContentBase,
    PageContentSection,
    PageHeaderActionItem,
    PageHeaderActions,
    PageHeaderApprovals,
    PhieuXuatKhoTable,
    // VatTuNgoaiDinhMucTable,
    VatTuNgoaiDinhMucTableV2,
    Can,
  },

  setup() {
    const router = useRouter();
    const { goBack, push, id: idBienBan } = useRouterCustom();

    const { ButtonTypeColors, ButtonsType } = useButtonCustom();

    const { canDuyetBbgt, canConfirmBbgt } = usePermission();

    const { endLoading, startLoading } = useLoading();

    const { moment } = useDateTime();

    const showConfirmationDeleteDialog = ref(false);

    const onPrintButtonClicked = () => {
      openBienBanGiaiThePdf(router, idBienBan.value);
    };

    const { approvalLevel } = useBienBan();

    return {
      BIEN_BAN_GIAI_THE,
      PHIEU_XUAT_KHO,
      approvalLevel,
      shouldShowConfirmButtonForLevel,
      shouldShowApproveButtonForLevel,
      ButtonTypeColors,
      ButtonsType,
      canConfirmBbgt,
      canDuyetBbgt,
      endLoading,
      goBack,
      idBienBan,
      moment,
      startLoading,
      onPrintButtonClicked,
      push,
      showConfirmationDeleteDialog,
      faFileCirclePlus,
      faPencil,
      faPrint,
      faTrash,
    };
  },

  data() {
    return {
      bienBanDetail: null as IBienBanGiaiTheResponseBase | null,
      bienBanParentDetail: null as IBienBanGiaiTheResponseBase | null,
      capSuaChuaDetail: null as ICapSuaChuaResponseBase | null,
      dinhMucCoDinhDetail: null as null | IDinhMucCoDinhNewResponseBase,
    };
  },

  async mounted() {
    await this.fetchBienBan();

    if (this.bienBanDetail?.id_ke_hoach_sua_chua) {
      setBreadcrumbLinks([
        {
          text: "Kế hoạch sửa chữa",
          routeData: {
            name: "chi-tiet-ke-hoach-sua-chua",
            params: {
              id: this.bienBanDetail?.id_ke_hoach_sua_chua,
            },
          },
        },
        {
          text: "BBGT",
        },
      ]);
    } else {
      setBreadcrumbLinks([
        {
          text: "Biên bản giải thể",
          routeData: {
            name: "danh-sach-bien-ban-giai-the",
          },
        },
        {
          text: "Chi tiết",
        },
      ]);
    }

    setCurrentPageTitle(
      `${this.bienBanDetail?.cap_sua_chua?.ten_cap_sc} (${this.bienBanDetail?.toa_xe?.ma_toa_xe} - ${this.bienBanDetail?.toa_xe?.ten_toa_xe})`
    );

    document.title = `${this.bienBanDetail?.so_bien_ban} - Biên bản giải thể - Đường sắt Hà Nội`;
  },

  computed: {
    idCapSuaChua() {
      return (
        this.bienBanDetail?.ke_hoach_sua_chua?.id_cap_sc ||
        this.bienBanDetail?.cap_sua_chua?.id ||
        0
      );
    },
    idDanhMucToaXe() {
      return (
        this.bienBanDetail?.ke_hoach_sua_chua?.id_danh_muc_toa_xe ||
        this.bienBanDetail?.toa_xe?.danh_muc_toa_xe?.[0]?.id ||
        0
      );
    },
    shouldShowConfirmButton() {
      return shouldShowConfirmButtonForLevel(
        this.bienBanDetail,
        this.approvalLevel
      );
    },
    shouldShowApproveButton() {
      return shouldShowApproveButtonForLevel(
        this.bienBanDetail,
        this.approvalLevel
      );
    },
    hasApprovalActions() {
      const canComplete =
        !this.bienBanDetail?.ngay_ra_xuong &&
        this.bienBanDetail?.is_admin_approved &&
        this.bienBanDetail?.is_approved;

      return (
        this.shouldShowApproveButton ||
        this.shouldShowConfirmButton ||
        Boolean(canComplete)
      );
    },
    canCreatePhieuXuat() {
      if (!this.bienBanDetail) return false;
      // Enable only if both is_approved and is_admin_approved are true
      return (
        this.bienBanDetail.is_approved === true &&
        this.bienBanDetail.is_admin_approved === true
      );
    },
    createPhieuXuatIdKho() {
      const idKhoFromPhieu = this.bienBanDetail?.phieu_xuat_kho[0]?.id_kho ?? 0;
      const idKhoFromQuery = Number(this.$route.query.idKho) || 0;

      return idKhoFromPhieu || idKhoFromQuery;
    },
    shouldShowApprovalNotice() {
      return this.approvalLevel > 0 && this.bienBanDetail;
    },
    approvalNoticeBody() {
      if (!this.bienBanDetail) return "";

      const parts: string[] = [];

      // Confirmation status
      if (this.approvalLevel === 2) {
        if (this.bienBanDetail.is_approved) {
          const approvedBy = this.bienBanDetail.approved_by?.ho_va_ten || "-";

          const approvedAt = this.bienBanDetail.approved_at
            ? this.moment(this.bienBanDetail.approved_at).format(
                "DD/MM/YYYY HH:mm"
              )
            : "-";

          parts.push(
            `<strong>Xác nhận:</strong> Đã xác nhận bởi ${approvedBy} vào ${approvedAt}`
          );
        } else {
          parts.push(`<strong>Xác nhận:</strong> Chưa xác nhận`);
        }
      }

      // Approval status
      if (this.bienBanDetail.is_admin_approved) {
        const approvedBy =
          this.bienBanDetail.admin_approved_by?.ho_va_ten || "-";

        const approvedAt = this.bienBanDetail.admin_approved_at
          ? this.moment(this.bienBanDetail.admin_approved_at).format(
              "DD/MM/YYYY HH:mm"
            )
          : "-";

        parts.push(
          `<strong>Duyệt:</strong> Đã duyệt bởi ${approvedBy} vào ${approvedAt}`
        );
      } else {
        parts.push(`<strong>Duyệt:</strong> Chưa duyệt`);
      }

      return parts.join("<br/>");
    },

    vatTuItemsDisplay(): IVatTuResponseBaseWithApprovedStatus[] {
      if (!this.bienBanDetail) return [];

      if (this.bienBanDetail.ngoai_dinh_muc?.length >= 0) {
        return this.bienBanDetail.ngoai_dinh_muc.map((vatTuItem) => ({
          ...vatTuItem,
          ...(vatTuItem.pivot.ghi_chu && {
            ghi_chu: vatTuItem.pivot.ghi_chu,
          }),
          is_approved: vatTuItem.pivot.is_approved === 1,
          so_luong: vatTuItem.pivot.so_luong,
        }));
      }

      return [];
    },
  },

  methods: {
    async fetchBienBan() {
      await this.startLoading();

      try {
        const {
          data: { data },
        } = await BienBanGiaiTheService.get(this.idBienBan);

        this.bienBanDetail = data;

        if (this.bienBanDetail.id_cha) {
          const {
            data: { data: parentData },
          } = await BienBanGiaiTheService.get(this.bienBanDetail.id_cha);

          this.bienBanParentDetail = parentData;
        }

        await this.endLoading();
      } catch (e) {
        this.endLoading();

        await swalErrNotification(e, "Có lỗi khi lấy thông tin biên bản");

        this.$router.push({
          name: "danh-sach-bien-ban-giai-the",
        });
      }
    },

    async confirmDelete() {
      const bienBanToDelete = this.bienBanDetail;
      this.showConfirmationDeleteDialog = false;

      try {
        await this.startLoading();

        await BienBanGiaiTheService.delete(bienBanToDelete.id);

        this.endLoading();

        await swalNotification(
          `Xoá biên bản số ${bienBanToDelete.so_bien_ban} thành công`,
          "success"
        );

        this.$router.push({
          name: "danh-sach-bien-ban-giai-the",
        });
      } catch (e) {
        this.endLoading();

        await swalErrNotification(e, "Có lỗi khi xoá biên bản");
      }
    },
  },
});
